/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { CommonModule, isPlatformBrowser, NgOptimizedImage, PlatformLocation } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  Signal,
  signal,
  TemplateRef,
  TransferState,
  ViewChild,
  ViewContainerRef,
  viewChild
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { isReadable, TinyColor } from '@ctrl/tinycolor';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTicket, faPlay, faTv, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import { ENVIRONMENT, MovieSiteHeaderImg } from '@environment';
import { AssetsLockerLinkComponent } from '@usheru-hff/access-locker/ui-assets-locker-link';
import { GsapAnimationService, MovieSiteConfettiMode, playMovieSiteConfetti } from '@usheru-hff/core/gsap';
import {
  MovieSiteConfigurationService,
  MovieSiteFooterComponent,
  MovieSiteNavActionComponent,
  MovieSiteTopMenuComponent,
  MovieSiteNavActionService,
  NavAction,
  NavLink,
  MovieSiteSideMenuComponent
} from '@usheru-hff/core/movie-site';
import {
  FeatureFilmInteractionsComponent,
  FeatureFilmInteractionsModule
} from '@usheru-hff/movie-detail/feature-film-interactions';
import { MovieDetailFeatureShowtimesModule } from '@usheru-hff/movie-detail/feature-showtimes';
import { ShowtimeStreamingComponent } from '@usheru-hff/movie-detail/feature-showtimes-streaming';
import { MovieDetailFeatureStreamingModule } from '@usheru-hff/movie-detail/feature-streaming';
import { MovieCompetitionComponent, MovieTellFriendsComponent } from '@usheru-hff/movie-detail/ui-movie-elements';
import { MOVIE_TRANSFER_KEY, MovieTranslationsService } from '@usheru-hff/sdk/movie';
import { StructuredDataService } from '@usheru-hff/sdk/structured-data';
import { generateMovieTrackingEvent, TRACKING_SERVICE } from '@usheru-hff/sdk/tracking';
import { getChannelMovieProp, Movie } from '@usheru-hff/shared/domain';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { FeatureFilmInteractionsToken } from '@usheru-hff/shared/tokens';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { getDateFromDDMMYYYY, LogService, ScopedLogger, TemplatesService } from '@usheru-hff/shared/utils';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { TitleService } from '@usheru-hff/shared/utils-title';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

import { BrandMovieService } from '../brand-movie.service';
import { textureConfig } from '../movie-site-background-texture';
import { SectionHyperLink } from '../feature-movie-awareness-page/feature-movie-awareness-page.component';
import { ChannelService } from '@usheru-hff/sdk/channel';

type tabs = 'showtimes' | 'streaming';
// Used for subscription component
enum CustomTabType {
  'showtimes' = 'in-cinemas',
  'streaming' = 'watch-at-home'
}

type TrailerButtonNavActionStyle = Extract<NavAction['style'], 'button-fill' | 'button-not-fill'>;

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'feature-movie-conversion-page',
  imports: [
    CommonModule,
    RouterLink,
    NgOptimizedImage,
    FontAwesomeModule,
    MovieTellFriendsComponent,
    SharedFeatureSocialLinksModule,
    MovieDetailFeatureShowtimesModule,
    MovieDetailFeatureStreamingModule,
    MovieTellFriendsComponent,
    MovieSiteTopMenuComponent,
    MovieSiteFooterComponent,
    MovieSiteNavActionComponent,
    ShowtimeStreamingComponent,
    MovieCompetitionComponent,
    FeatureFilmInteractionsModule
  ],

  templateUrl: './feature-movie-conversion-page.component.html',
  styleUrl: './feature-movie-conversion-page.component.scss',
  providers: [BrandMovieService]
})
export class FeatureMovieConversionPageComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly movie = input<Movie | undefined>(undefined);

  protected readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private readonly PLATFORM_ID = inject(PLATFORM_ID);
  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);
  private readonly platformLocation = inject(PlatformLocation);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly brandMovieService = inject(BrandMovieService);
  private readonly channelService = inject(ChannelService);
  private readonly movieSiteConfig = inject(MovieSiteConfigurationService);
  private readonly navActionService = inject(MovieSiteNavActionService);
  private readonly showtimeStreamingService = inject(ShowtimeStreamingService);
  private readonly videoService = inject(VideoFullscreenService);
  private readonly metaTagsService = inject(MetatagsService);
  private readonly imgService = inject(ImageService);
  private readonly translationsService = inject(TranslationsService);
  private readonly env = inject(ENVIRONMENT);
  private readonly transferState = inject(TransferState);
  private readonly titleService = inject(TitleService);
  private readonly trackingService = inject(TRACKING_SERVICE);
  private readonly structuredDataService = inject(StructuredDataService);
  private readonly movieTranslationsService = inject(MovieTranslationsService);
  private readonly templatesService = inject(TemplatesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gsapAnimationService = inject(GsapAnimationService);
  private readonly confettiContainer = viewChild<ElementRef<HTMLElement>>('movieSiteConfetti');
  private confettiStarted = false;

  readonly logoExternalLink: string | undefined = this.env?.movieAwarenessLogoExternalLink;

  @HostBinding('style') bindingStyle: {
    [rule: string]: string;
  } = {};
  @ViewChild(MovieSiteSideMenuComponent) sidemenuComponent!: MovieSiteSideMenuComponent;
  @ViewChild('footerWrapper') footerWrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('filmInteractionComponentDesktop', { read: ViewContainerRef })
  filmInteractionComponentDesktop!: ViewContainerRef;
  @ViewChild('filmInteractionComponentMobile', { read: ViewContainerRef })
  filmInteractionComponentMobile!: ViewContainerRef;

  private filmInteractionComponentRef!: ComponentRef<FeatureFilmInteractionsComponent>;
  private filmInteractionComponent2Ref!: ComponentRef<FeatureFilmInteractionsComponent>;

  readonly desktopBreakpoint = 1200;
  private readonly logger: ScopedLogger = inject(LogService).scoped('movie-site-conversion');

  filmInteractionIsEnabled = signal(false);

  readonly hasCompetition = computed(() => this.brandMovieService.checkMovieCompetition(this.movie()));
  readonly showCompetitionInConversion = computed(() =>
    this.brandMovieService.shouldShowCompetitionInConversion(this.movie())
  );

  tab = signal<tabs>('showtimes');
  get hasShowtimes() {
    return this.movie() && this.showtimeStreamingService.determineInCinemasAvailability(this.movie()!);
  }

  get hasStreaming() {
    return this.movie() && this.showtimeStreamingService.determineStreamingAvailability(this.movie()!);
  }

  releaseDate = computed(() => {
    const release = this.movie()?.releaseDate;
    return release ? getDateFromDDMMYYYY(release) || undefined : undefined;
  });
  channelName = signal<string | undefined>(undefined);
  mainLogoUrl = '';
  isBrandColorReadable = signal<boolean>(true);
  navActions = computed<NavAction[]>(() => this.generateNavActions());
  customNavigationButtonActions = computed<NavAction[]>(() =>
    this.navActionService.insertCustomNavigationButton([], this.movieSiteConfig.customNavigationButtonConfiguration())
  );
  sectionsHyperLink = signal<SectionHyperLink[]>([
    {
      label: $localize`:@@movie.menu.film-info:Film info`,

      placeholder: 'Film Info',

      ref: undefined
    }
  ]);

  readonly navActionTrailer = computed<NavAction>(() => ({
    type: 'command',
    label: $localize`:@@movie.menu.trailer:Trailer`,
    labelKey: 'movie.menu.trailer',
    placeholder: 'Trailer',
    icon: faPlay,
    command: () => this.playTrailer(),
    style: this.getTrailerButtonNavActionStyle()
  }));

  readonly navActionFilmInfo: NavLink = {
    type: 'link',
    label: $localize`:@@movie.menu.film-info:Film info`,
    labelKey: 'movie.menu.film-info',
    placeholder: 'Film Info',
    url: ['..', 'film-info'],
    fragment: 'landing',
    style: 'button-fill',
    icon: faArrowLeft
  };

  readonly navActionCompetition = computed<NavAction>(() => ({
    type: 'link',
    label: $localize`:@@movie.cta.enterNowToWin:Enter now to win`,
    labelKey: 'movie.cta.enterNowToWin',
    placeholder: 'Enter now to win',
    url: this.showCompetitionInConversion() ? [] : ['..', 'film-info'],
    fragment: 'competition',
    style: 'button-fill'
  }));

  hideLogoTitle = false;
  hideTitleText = false;

  inCinemaAddon!: TemplateRef<unknown>;
  appHasOnlyMovies = false;

  customHeaderImages = signal<MovieSiteHeaderImg[] | undefined>(undefined);
  readonly footerComponent = computed(() => this.movieSiteConfig.footerConfiguration()?.component || undefined);

  readonly TrackingCategory = 'MovieSite';
  faPlay = faPlay;
  faTicket = faTicket;
  faTv = faTv;
  fafaArrowLeft = faArrowLeft;

  @HostListener('window:resize')
  onResize() {
    this.setShowtimesheaderHeight();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.checkStykyStyle();

    // this.setFixForHeaderBar();
  }

  get element() {
    return this.elementRef.nativeElement;
  }

  readonly ASSETSLOCKER_LABEL = 'movie-site.assets-locker-label';
  readonly ASSETSLOCKER_LABEL_BUTTON = 'movie-site.assets-locker';

  readonly movieTranslations = this.movieTranslationsService.translations;
  readonly hasMovieTranslationsBeenLoaded = this.movieTranslationsService.hasLoadedTranslations;
  readonly backgroundTexture = computed(() =>
    getChannelMovieProp('movieSite.background.texture', this.channelService.channel, this.movie())
  );
  readonly backgroundTextureConfig = computed(() => {
    const texture = this.backgroundTexture();
    return texture ? textureConfig[texture] : null;
  });
  readonly confettiMode = computed<MovieSiteConfettiMode | undefined>(() => {
    const movie = this.movie();
    if (!movie || this.movieSiteConfig.pageConfiguration()?.disableScrollAnimations) return undefined;

    const mode = getChannelMovieProp('movieSite.gsap.confetti', this.channelService.channel, movie);
    return mode === 'enabled' ? mode : undefined;
  });

  constructor(@Optional() private featureFilmInteractionToken: FeatureFilmInteractionsToken) {
    // Prepare configuration setting
    effect(
      () => {
        if (!this.movie()) return;
        this.movieSiteConfig.prepareService(this.movie()!, 'book-now');
      },
      { allowSignalWrites: true }
    );

    effect(() =>
      this.structuredDataService.setMovieJsonLd(
        this.movie(),
        this.isPlatformBrowser ? this.platformLocation.href : undefined
      )
    );

    this.movieSiteConfig.effectMetaTitleFromMovieTranslations({
      movie: this.movie,
      translations: this.movieTranslations,
      hasLoaded: this.hasMovieTranslationsBeenLoaded
    });

    afterNextRender(() => {
      void this.startMovieSiteConfetti();
    });

    effect(() => {
      this.confettiMode();
      this.confettiContainer();
      if (!this.isPlatformBrowser) return;
      void this.startMovieSiteConfetti();
    });
  }

  ngOnInit() {
    const movie = this.movie();
    this.movieSiteConfig.prepareService(movie!, 'book-now');

    this.appHasOnlyMovies = this.env?.appHasOnlyMovies || false;
    // this.brandMovieService.checkMovieInRouteState();

    // check if route is 'book-now' to show showtimes tab and 'watch-now' to show streaming tab
    const urlPart = this.route.snapshot.url[this.route.snapshot.url.length - 1].path || 'book-now';

    this.changeTab(
      urlPart === 'book-now' && this.hasShowtimes
        ? 'showtimes'
        : urlPart === 'watch-now' && this.hasStreaming
          ? 'streaming'
          : 'showtimes'
    );

    this.bindingStyle = this.setColorStyles();
    this.setEnvConfiguration();
    this.isBrandColorReadable.set(this.isRedeableBrandOverBackgroundColor());
    //
    this.setChannelLogo();
  }

  ngAfterViewInit() {
    this.checkTab();
    this.setInCinmeaAddon();
    if (this.movie()?.faviconImageUrl) {
      this.titleService.setFavicon(this.movie()?.faviconImageUrl);
    }

    this.loadCommunityElements();
  }

  loadCommunityElements() {
    const movie = this.movie();
    if (movie == null) {
      this.debugLog('Skipping community elements load: movie is null');
      return;
    }

    if (this.featureFilmInteractionToken) {
      this.debugLog('Feature film interaction token is available');
      this.filmInteractionIsEnabled.set(true);

      if (!this.filmInteractionComponentDesktop) {
        this.filmInteractionComponentRef.instance.movie = movie;
      } else {
        this.filmInteractionComponentRef = this.filmInteractionComponentDesktop.createComponent(
          FeatureFilmInteractionsComponent
        );
        this.filmInteractionComponentRef.instance.movie = movie;
        this.filmInteractionComponentRef.instance.orientation = 'horizontal';
        this.filmInteractionComponentRef.instance.classBinding = 'movie-site-film-interactions-desktop';
      }

      if (!this.filmInteractionComponentMobile) {
        this.filmInteractionComponent2Ref.instance.movie = movie;
      } else {
        this.filmInteractionComponent2Ref = this.filmInteractionComponentMobile.createComponent(
          FeatureFilmInteractionsComponent
        );
        this.filmInteractionComponent2Ref.instance.movie = movie;
        this.filmInteractionComponent2Ref.instance.orientation = 'horizontal';
        this.filmInteractionComponent2Ref.instance.classBinding = 'movie-site-film-interactions-mobile';
      }
    } else {
      this.debugLog('Community film interaction token/component not available');
    }
  }

  setEnvConfiguration() {
    const configs = this.env?.movieSiteConfig;
    if (!configs) return;
    const movie = this.movie()?.friendlyUrl || '';
    const config = configs[movie];
    if (!config) return;

    this.setConfigHideTitleLogo(config.conversionHideTitleLogo);
    this.setConfigHideTitleText(config.conversionHideTitleText);
  }

  setConfigHideTitleLogo(value?: boolean) {
    if (!value) return;
    this.hideLogoTitle = value;
  }

  setConfigHideTitleText(value?: boolean) {
    if (!value) return;
    this.hideTitleText = value;
  }

  setChannelLogo() {
    this.channelName.set(this.channelService.channel.name);
    this.mainLogoUrl = this.channelService.channel.logoUrl;
  }

  setServiceSettings() {
    this.brandMovieService.sidemenuRef = this.sidemenuComponent;
  }

  setColorStyles() {
    const brandColor = this.movie()?.properties?.theme?.mainColor || '#000000';
    const backgroundColor = this.movie()?.properties?.theme?.backgroundColor || '#ffffff';
    const borderRadius = this.movie()?.properties?.selectShowtimeContainerBorderRadius || '12px';
    const customBrandContrast = this.movie()?.properties?.theme?.mainColorContrast;
    const customBackgroundContrast = this.movie()?.properties?.theme?.backgroundColorContrast;
    return this.brandMovieService.setColorStyles({
      brandColor,
      backgroundColor,
      borderRadius,
      customBrandContrast,
      customBackgroundContrast
    });
  }

  // injectStyles() {
  //   if (!this.isPlatformBrowser) return;
  //   const fontFamily = this.movie()?.properties?.theme?.fontFile || 'Arial';
  //   const fontFamilyBold = this.movie()?.properties?.theme?.boldFontFile || 'Arial';
  //   this.brandMovieService.injectStyles(fontFamily, fontFamilyBold);
  // }

  private setShowtimesheaderHeight() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const header = document.querySelector('.ush-showtimes-header') as HTMLElement;
    if (!header) return;
    const height = header.clientHeight;
    host.style?.setProperty('--showtimes-header-height', `${height}px`);
  }

  checkStykyStyle() {
    const host = this.elementRef.nativeElement as HTMLElement;
    const header = document.querySelector('.ush-showtimes-header') as HTMLElement;
    const wrapper = document.querySelector('.ush-showtimes-wrapper') as HTMLElement;
    if (!header || !wrapper) return;
    if (header.offsetTop !== wrapper.offsetTop) {
      host.classList.add('stycked');
    } else {
      host.classList.remove('stycked');
    }
  }

  get isDesktop() {
    return window.innerWidth >= this.desktopBreakpoint;
  }

  playTrailer() {
    this.videoService.playWithConsent({
      content: this.movie() as Movie,
      category: this.TrackingCategory,
      animate: true,
      videoSettings: { muted: false, autoplay: true }
    });
  }

  private getTrailerButtonNavActionStyle(): TrailerButtonNavActionStyle {
    return this.movie()?.properties?.movieSite?.conversion?.trailerButtonFilled ? 'button-fill' : 'button-not-fill';
  }

  setMetaTags(movie?: Movie): void {
    if (!movie) {
      this.metaTagsService.resetMetaTags();
      return;
    }

    //

    const metaTags: MetaTags = {
      url: this.platformLocation.pathname,
      title: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.title`)
        ? `movie.${movie.friendlyUrl}.title`
        : $localize`:@@meta.page.movie-detail.title:_channel_ - _movie_`,

      description: this.translationsService.keyExists(`movie.${movie.friendlyUrl}.description`)
        ? `movie.${movie.friendlyUrl}.description`
        : $localize`:@@meta.page.movie-detail.description:Movie detail`,
      video: movie.trTrailerLink
    };

    metaTags.favicon = movie?.faviconImageUrl;
    if (movie.tilePhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.tilePhotoUrl, { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imgService.computeUrl(movie.portraitPhotoUrl, { height: 1000 });
    }
    //TODO cases with more directors?
    const director = movie?.movieCrew?.find(mc => {
      if (!mc.crewRole?.name) return false;

      return mc.crewRole.name.toLowerCase() == 'director';
    })?.star?.name;
    this.metaTagsService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  checkTab() {
    const path = this.route.snapshot.routeConfig?.path;
    if (path?.includes('book-now') && this.hasShowtimes) {
      this.tab.set('showtimes');
    } else if (path?.includes('watch-now') && this.hasStreaming) {
      this.tab.set('streaming');
    }
  }

  changeTab(tab: tabs) {
    const newPath = tab === 'showtimes' ? 'book-now' : 'watch-now';
    // check if router has the 'book-now' or 'watch-now' path and replace it with the new one

    if (this.router.url.includes('book-now') || this.router.url.includes('watch-now')) {
      this.router.navigateByUrl(this.router.url.replace(/(book-now|watch-now)/, newPath), {
        replaceUrl: true
      });
    } else {
      this.router.navigateByUrl(`${this.router.url}/${newPath}`, {
        replaceUrl: true
      });
    }
    this.tab.set(tab);
  }

  isRedeableBrandOverBackgroundColor() {
    const brandColor = new TinyColor(this.movie()?.properties?.theme?.mainColor || '#000000');
    const backgroundColor = new TinyColor(this.movie()?.properties?.theme?.backgroundColor || '#ffffff');
    return isReadable(brandColor, backgroundColor);
  }

  setInCinmeaAddon() {
    this.inCinemaAddon = this.templateService.get('brandedInCinemasAddon');
  }

  get templateService() {
    return this.templatesService;
  }

  get hasAssets() {
    return this.movie()?.hasAssets || false;
  }

  /**
   * Method that will track ShowtimesLoaded action if ther are performances present.
   * Tracking is emitted from in-cinemas source-of-truth component.
   */
  onShowtimesLoaded() {}

  onStreamingPlatformsLoaded() {
    const movie = this.movie();
    if (!movie) return;

    const dedupeKey = this.buildStreamingLoadedDedupeKey(movie);
    const tracked = this.trackingService.trackEventOnce(
      generateMovieTrackingEvent(movie, 'StreamingLoaded', 'MovieStreaming'),
      dedupeKey
    );
    this.debugLog(tracked ? 'Tracking StreamingLoaded' : 'Skipping duplicate StreamingLoaded', {
      movie: movie.title,
      dedupeKey
    });
  }

  private buildStreamingLoadedDedupeKey(movie: Movie): string {
    return [
      'StreamingLoaded',
      `movie:${movie.id ?? movie.friendlyUrl ?? ''}`,
      `language:${this.translationsService.currentLanguage()?.code ?? ''}`,
      'source:movie-site-conversion'
    ].join('|');
  }

  onClickFilmInfo() {
    const movie = this.movie();
    if (movie) {
      // The tracking action must be different if uncommented
      // this.trackingService.trackEvent(generateMovieTrackingEvent(movie, 'FilmInfoView', this.TrackingCategory));
    }
  }

  // NEW CODE TO CLEAN AFTER
  generateNavActions() {
    const navActions: NavAction[] = [];
    // Add Home
    navActions.push({
      ...this.navActionFilmInfo,
      url: this.navActionFilmInfo.url,
      type: 'link',
      icon: undefined,
      style: 'inline'
    });

    return navActions;
  }

  get subscriptionType() {
    return CustomTabType[this.tab()] || 'in-cinemas';
  }

  private debugLog(step: string, context?: unknown): void {
    this.logger.log(step, context ?? '');
  }

  private startMovieSiteConfetti(): void {
    if (this.confettiStarted || !this.isPlatformBrowser || !this.gsapAnimationService.canAnimate()) return;

    const mode = this.confettiMode();
    const container = this.confettiContainer()?.nativeElement;
    if (!mode || !container) return;

    this.confettiStarted = true;
    void playMovieSiteConfetti(container, this.destroyRef, this.gsapAnimationService);
  }

  ngOnDestroy(): void {
    this.movieSiteConfig.resetService();
    this.metaTagsService.resetMetaTags();
    this.brandMovieService.removeStyles();
    this.brandMovieService.restoreFooter();
    this.structuredDataService.removeJsonLdScript();
    this.transferState.remove(MOVIE_TRANSFER_KEY);
  }
}
